import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useDisconnect, useAccount, useSwitchNetwork, useNetwork } from "wagmi";

import { removeUserInfo, removeUserToken, setUserInfo } from "../../../redux/user/action";
import { clearScoutsData } from "../../../redux/scout/action";
import { clearMessages, clearChannels } from "../../../redux/chat/actions";
import Network from "../../../utils/Network";
import useToast from "../../../hooks/useToast";
import { clearTour } from "../../../redux/tour/action";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

const axios = new Network();

const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { disconnect } = useDisconnect();
  const { address, isConnected, isDisconnected } = useAccount();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { chains, chain } = useNetwork();
  const openToast = useToast();
  const { t } = useAppTranslation("errors");

  /**
   * Get current user information
   */
  const getMe = useCallback(async () => {
    const response = await axios.CallApi({
      url: "/player",
      method: "get",
    });
    
    if (response.ok) {
      const data = response.data as any;
      dispatch(setUserInfo(data));
      
      // Check user state
      if (data.state === 2 || data.state === 3) {
        logout();
      }
      
      return data;
    }
    
    return null;
  }, [dispatch]);

  /**
   * Log out the current user
   */
  const logout = useCallback(() => {
    // Clear redux data
    dispatch(removeUserInfo());
    dispatch(removeUserToken());
    dispatch(clearScoutsData());
    dispatch(clearMessages());
    dispatch(clearChannels());
    dispatch(clearTour());
    // Clear localStorage
    localStorage.removeItem("user.token");
    
    // Handle wallet disconnect
    disconnect?.();
    
    // Navigate to login page
    if (location.pathname !== "/login") {
      navigate(
        "/login" +
          `?return=${location.pathname}${
            location.search ? location.search : ""
          }`
      );
    }
  }, [dispatch, disconnect, navigate, location]);

  /**
   * Check if the network is correct for Wallet Connect
   */
  const checkNetworkWagmi = useCallback(async () => {
    const connectMethod = localStorage.getItem("connector");
    
    if (
      !isDisconnected &&
      chain?.id !== 56 && // BSC chain ID
      connectMethod === "walletconnect" &&
      switchNetworkAsync
    ) {
      try {
        openToast({
          ok: false,
          message: t("errors:defaultLayout.invalidNetwork"),
        });
        await switchNetworkAsync(chains[0]?.id);
        return true;
      } catch (e) {
        logout();
        return false;
      }
    }
    
    return true;
  }, [chain, chains, isDisconnected, logout, openToast, switchNetworkAsync]);

  /**
   * Check if the wallet address matches the user info
   */
  const checkWalletMatch = useCallback(() => {
    const userInfo = JSON.parse(localStorage.getItem("persist:root") || "{}").userInfo;
    const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
    const connectMethod = localStorage.getItem("connector");
    
    if (
      parsedUserInfo &&
      connectMethod === "walletconnect" &&
      isConnected &&
      address
    ) {
      if (parsedUserInfo?.walletAddress.toLowerCase() !== address.toLowerCase()) {
        openToast({ ok: false, message: t("errors:defaultLayout.walletMismatch") });
        logout();
        return false;
      }
    }
    
    return true;
  }, [address, isConnected, logout, openToast, t]);

  /**
   * Check if the user is authenticated
   */
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem("user.token");
  }, []);

  /**
   * Initialize auth on component mount
   */
  const initAuth = useCallback(async () => {
    if (!isAuthenticated()) {
      navigate(
        "/login" +
          `?return=${location.pathname}${
            location.search ? location.search : ""
          }`
      );
      return false;
    }
    
    // Get user info
    await getMe();
    
    // Check wallet
    const walletMatches = checkWalletMatch();
    if (!walletMatches) return false;
    
    // Check network
    return checkNetworkWagmi();
  }, [getMe, checkWalletMatch, checkNetworkWagmi, isAuthenticated, navigate, location]);

  // Effect for checking network when chain changes
  useEffect(() => {
    const connectMethod = localStorage.getItem("connector");
    if (connectMethod === "walletconnect" && chain) {
      checkNetworkWagmi();
    }
  }, [chain, checkNetworkWagmi]);

  return {
    getMe,
    logout,
    isAuthenticated,
    initAuth,
    checkWalletMatch,
    checkNetworkWagmi
  };
};

export default useAuth; 
